// 필드 HUD(레인 F): 구역명·청크 좌표, 균열 조각 수, 이벤트 알림 띠(위쪽 가장자리), 미니맵(활성 3×3·이벤트·균열 입구·귀환석·숨은 던전),
// 귀환석 대기 막대, 균열 진행 게이지·층·남은 시간. 필드·균열 장면에서만 보인다. DOM은 한 번만 만들고 값이 바뀔 때만 글자를 고친다.
// 미니맵 바탕(구역 바닥)은 처음 한 번 오프스크린 캔버스에 그리고, 매 프레임은 그 그림 + 점 몇 개만 그린다.
import { Scene, type GameCommand, type GameView } from '../shared/types';
import { FIELD_GRID_SPEC as G, RIFT } from '../shared/field/constants';
import { fieldZone1 } from '../shared/field/layout';
import { FieldEventKind, RiftPhase, type RiftView } from '../shared/field/view';
import { C, FONT, el, designScale } from './dom';
import { uiSizeFactor } from './uiSize';
import { mapRect, viewH, viewRotated, viewW } from '../shared/viewport';
import { type BandRect, riftLayoutChanged, setKey, riftBandCue, choiceInputsChanged, BAND_INFO_ROW_H, RIFT_HUD_H, riftHudTop, bandLayout, columnSlot, riftHudSlot, topBandSlot, TOP_BAND_H1, choiceModel, griftClock, griftHead, resultText, BAND_BTN_MIN, BAND_GAP, BAND_PAD, BAND_ROW_H } from './griftBand';
import { touchControlRects, touchHudLayout } from './skillHudLayout';
import { isTouchDevice } from './touch';
/** 키보드 화면에서 입구 선택 띠가 비켜야 할 바닥 HUD */
/** 터치 조작이 실제로 놓은 버튼 자리(TouchControls가 준다) */
export interface TouchRects { readonly controlRects: readonly BandRect[]; readonly layoutVersion: number }
function setBtn(b: HTMLButtonElement, on: boolean): void { b.disabled = !on; b.style.opacity = on ? '1' : '.4'; b.setAttribute('aria-disabled', on ? 'false' : 'true'); }
const NO_RECTS: readonly BandRect[] = [];
const GRIFT_KB_AVOID = ['skillbar', 'awaken-gauge'] as const;

const MAP_PX = 132;
const BAND_MS = 4500;
/** 알림 띠가 비켜 갈 위 줄 HUD(data-testid) */
const TOP_BAND_AVOID = ['hud-top-left', 'hud-top-right', 'hud-minimap', 'menu-settings', 'camera-reset', 'hud-xp'] as const;
/** 조작 사각형 + 각성 게이지 글자 자리(터치 배치 사각형에는 막대만 들어 있다) */
function riftControls(p: ReturnType<typeof touchHudLayout>): Array<{ x: number; y: number; w: number; h: number }> {
  return touchControlRects(p).map((r, i, a) => (i === a.length - 1 ? { x: r.x, y: r.y - RIFT_GAUGE_LABEL, w: r.w, h: r.h + RIFT_GAUGE_LABEL } : r));
}
/** 각성 게이지 막대 위 '각성 n%' 글자 높이(터치 배치 사각형에는 막대만 들어 있다) */
const RIFT_GAUGE_LABEL = 20;
const COLS = 'ABCDE';

export class FieldHud {
  private readonly root: HTMLDivElement;
  private readonly zone: HTMLDivElement;
  private readonly shards: HTMLDivElement;
  private readonly eventLine: HTMLDivElement;
  private readonly hold: HTMLDivElement;
  private readonly holdBar: HTMLDivElement;
  private readonly band: HTMLDivElement;
  private readonly rift: HTMLDivElement;
  private readonly riftHead: HTMLDivElement;
  private readonly riftBar: HTMLDivElement;
  private readonly riftTime: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private bg: HTMLCanvasElement | null = null;
  private readonly mapBtn: HTMLButtonElement;
  private dispMode = -1;
  private lastZone = '';
  private lastShards = -1;
  private lastEvent = '';
  private lastRift = '';
  private lastHold = -1;
  private eventSerial = -1;
  private bandUntil = 0;
  private lastBand = '';
  private frame = 0;
  private lastChunk = -2;
  private lastEvKind = -1;
  private lastEvSec = -1;
  private lastU = -1;
  private lastRiftKey = -1;
  /** 직전에 본 균열 단계·통로 상태(알림 띠는 바뀌는 순간 한 번만) */
  private cuePhase = -1;
  private cuePortal = false;
  // 대균열 입구 선택 띠(C12): DOM은 한 번만, 값(열쇠·조각·단계·크기)이 바뀔 때만 고친다
  private readonly choice: HTMLDivElement;
  private readonly cNormal: HTMLButtonElement;
  private readonly cMinus: HTMLButtonElement;
  private readonly cTier: HTMLDivElement;
  private readonly cPlus: HTMLButtonElement;
  private readonly cInfo: HTMLDivElement;
  private readonly choiceR2: HTMLDivElement;
  private readonly choiceR3: HTMLDivElement;
  private choiceRows = 2;
  private readonly keyboardHud = !isTouchDevice(new URLSearchParams(typeof location === 'undefined' ? '' : location.search));
  private readonly cGo: HTMLButtonElement;
  private gTier = 0;
  private touchVer = -2;
  private readonly choiceKey = new Float64Array(6).fill(-1);
  private choiceOpen = false;
  private readonly sizeKey = new Float64Array(7).fill(-1);
  private readonly kbObs: BandRect[] = [{ x: 0, y: 0, w: 0, h: 0 }, { x: 0, y: 0, w: 0, h: 0 }];
  private readonly kbEls: Array<HTMLElement | null> = [null, null];
  private readonly fieldKey = new Float64Array(5).fill(-1);
  private lastResult = -1;
  /** 미니맵 축척(월드 m → 캔버스 px)과 원점 — drawMap 안에서 클로저를 만들지 않으려고 필드로 둔다 */
  private mapScale = 1;
  private mapOx = 0;
  private mapOz = 0;

  private readonly parentEl: HTMLElement;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void, private readonly touch: TouchRects | null = null) {
    this.parentEl = parent;
    const r = el('div', 'position:absolute;right:8px;top:118px;display:none;flex-direction:column;align-items:flex-end;gap:6px;pointer-events:none;z-index:30;' +
      'font:800 13px ' + FONT + ';color:#f4ecd8;text-shadow:0 1px 3px rgba(0,0,0,.9);transform-origin:100% 0;', parent);
    r.dataset.testid = 'field-hud';
    this.root = r;
    const panel = 'background:linear-gradient(180deg,rgba(20,26,40,.88),rgba(8,12,24,.88));border:1.5px solid rgba(185,138,42,.85);border-radius:6px;padding:4px 9px;';
    this.zone = el('div', panel + 'font:900 13px ' + FONT + ';color:' + C.gold + ';', r);
    this.zone.dataset.testid = 'field-zone';
    const mapBox = el('div', 'position:relative;width:' + MAP_PX + 'px;height:' + MAP_PX + 'px;border:2px solid rgba(185,138,42,.9);border-radius:6px;overflow:hidden;background:#0b1018;box-shadow:0 2px 8px rgba(0,0,0,.6);', r);
    this.canvas = el('canvas', 'width:100%;height:100%;display:block;', mapBox);
    this.canvas.width = MAP_PX * 2; this.canvas.height = MAP_PX * 2;
    this.canvas.dataset.testid = 'field-minimap';
    this.ctx = this.canvas.getContext('2d');
    this.mapBtn = el('button', 'position:absolute;right:3px;bottom:3px;pointer-events:auto;font:800 11px ' + FONT + ';color:#f4ecd8;background:rgba(20,26,40,.9);border:1px solid #b98a2a;border-radius:4px;padding:1px 6px;cursor:pointer;', mapBox, '지도 M');
    this.mapBtn.dataset.testid = 'field-map-open';
    this.mapBtn.addEventListener('click', () => send({ type: 'field', op: 'map' }));
    this.shards = el('div', panel + 'display:flex;align-items:center;gap:6px;', r);
    this.shards.dataset.testid = 'field-shards';
    this.eventLine = el('div', panel + 'display:none;color:#ffd0c0;', r);
    this.eventLine.dataset.testid = 'field-event';
    this.hold = el('div', panel + 'display:none;width:132px;box-sizing:border-box;', r, '귀환 중…');
    const hb = el('div', 'margin-top:3px;height:5px;background:rgba(255,255,255,.15);border-radius:3px;overflow:hidden;', this.hold);
    this.holdBar = el('div', 'height:100%;width:0;background:#7fd4ff;', hb);
    // 이벤트 알림 띠(위쪽 가운데)
    this.band = el('div', 'position:absolute;left:50%;top:4px;transform:translateX(-50%);display:none;box-sizing:border-box;max-width:min(520px,calc(100% - 16px));overflow:hidden;text-overflow:ellipsis;height:36px;line-height:32px;padding:0 16px;border-radius:6px;' +
      'pointer-events:none;z-index:41;white-space:nowrap;background:linear-gradient(180deg,rgba(40,16,16,.94),rgba(18,8,8,.94));border:2px solid #c0503a;' +
      'font:900 16px ' + FONT + ';color:#fff0e0;text-shadow:0 1px 3px rgba(0,0,0,.9);', parent);
    this.band.dataset.testid = 'field-band';
    this.band.setAttribute('role', 'status');
    // 균열 게이지: 필드 HUD와 같은 오른쪽 기둥(위쪽 가운데는 보스·대상 체력 막대 자리라 피한다)
    this.rift = el('div', 'position:absolute;right:8px;top:118px;display:none;flex-direction:column;align-items:stretch;gap:4px;width:220px;box-sizing:border-box;pointer-events:none;z-index:31;' +
      'padding:6px 9px;border-radius:6px;background:linear-gradient(180deg,rgba(30,18,52,.9),rgba(12,8,26,.9));border:1.5px solid #a070ff;transform-origin:100% 0;' +
      'font:800 13px ' + FONT + ';color:#f0e4ff;text-shadow:0 1px 3px rgba(0,0,0,.9);', parent);
    this.rift.dataset.testid = 'rift-hud';
    const row = el('div', 'display:flex;justify-content:space-between;width:100%;', this.rift);
    this.riftHead = el('div', '', row);
    this.riftTime = el('div', 'font-variant-numeric:tabular-nums;', row);
    const bar = el('div', 'width:100%;height:10px;border:1.5px solid #a070ff;border-radius:5px;background:rgba(20,10,40,.85);overflow:hidden;', this.rift);
    this.riftBar = el('div', 'height:100%;width:0;background:linear-gradient(90deg,#7040ff,#d090ff);', bar);
    this.riftBar.dataset.testid = 'rift-gauge';
    // 입구 선택 띠: 화면 아래 가장자리(조이스틱·스킬 버튼 사이 빈 자리). 좌표는 layoutChoice가 정한다
    const ch = el('div', 'position:absolute;left:0;top:0;display:none;flex-direction:column;gap:' + BAND_GAP + 'px;box-sizing:border-box;padding:' + BAND_PAD + 'px;z-index:42;pointer-events:auto;' +
      'touch-action:none;user-select:none;-webkit-user-select:none;border-radius:8px;background:linear-gradient(180deg,rgba(30,18,52,.94),rgba(12,8,26,.94));border:2px solid #a070ff;' +
      'font:800 13px ' + FONT + ';color:#f0e4ff;text-shadow:0 1px 3px rgba(0,0,0,.9);', parent);
    ch.dataset.testid = 'grift-band';
    this.choice = ch;
    const r1 = el('div', 'display:flex;gap:' + BAND_GAP + 'px;height:' + BAND_ROW_H + 'px;', ch);
    const r2 = el('div', 'display:flex;gap:' + BAND_GAP + 'px;height:' + BAND_ROW_H + 'px;align-items:stretch;', ch);
    const btn = (p: HTMLElement, label: string, css: string, id: string, fn: () => void): HTMLButtonElement => {
      const b = el('button', 'pointer-events:auto;touch-action:none;cursor:pointer;box-sizing:border-box;min-width:' + BAND_BTN_MIN + 'px;min-height:' + BAND_BTN_MIN + 'px;padding:0 8px;border-radius:6px;' +
        'border:1.5px solid #b98a2a;background:linear-gradient(180deg,#3a2a66,#1c1236);color:#f4ecd8;font:900 14px ' + FONT + ';' + css, p, label);
      b.type = 'button'; b.dataset.testid = id;
      let armed = false;
      b.addEventListener('pointerdown', (e) => { e.stopPropagation(); if (b.disabled) return; armed = true; });
      b.addEventListener('pointerup', (e) => { e.stopPropagation(); if (armed && !b.disabled) fn(); armed = false; });
      b.addEventListener('pointercancel', () => { armed = false; });
      b.addEventListener('pointerleave', () => { armed = false; });
      b.addEventListener('click', (e) => { if (e.detail === 0 && !b.disabled) fn(); });  // 키보드 Enter·Space
      return b;
    };
    this.cNormal = btn(r1, '', 'flex:1 1 auto;', 'grift-normal', () => send({ type: 'field', op: 'rift' }));
    btn(r1, '닫기', 'flex:0 0 auto;', 'grift-close', () => send({ type: 'field', op: 'gclose' }));
    this.cMinus = btn(r2, '−', 'flex:0 0 auto;font-size:20px;', 'grift-minus', () => { this.gTier--; this.choiceKey.fill(-1); });
    this.cTier = el('div', 'flex:0 0 auto;min-width:52px;display:flex;align-items:center;justify-content:center;font:900 15px ' + FONT + ';color:#ffd877;font-variant-numeric:tabular-nums;', r2);
    this.cTier.dataset.testid = 'grift-tier';
    this.cPlus = btn(r2, '+', 'flex:0 0 auto;font-size:20px;', 'grift-plus', () => { this.gTier++; this.choiceKey.fill(-1); });
    this.cInfo = el('div', 'flex:1 1 auto;min-width:0;display:flex;align-items:center;font:700 11px ' + FONT + ';line-height:1.25;color:#d8c8f0;white-space:pre-line;', r2);
    this.cInfo.dataset.testid = 'grift-info';
    this.choiceR2 = r2;
    // 좁은 화면(3줄 띠)에서 안내 글이 들어가는 셋째 줄
    this.choiceR3 = el('div', 'display:none;height:' + BAND_INFO_ROW_H + 'px;', ch);
    this.cGo = btn(r2, '도전', 'flex:0 0 auto;min-width:56px;border-color:#ffd877;background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;text-shadow:none;', 'grift-go', () => send({ type: 'field', op: 'grift', tier: this.gTier }));
  }

  private drawBg(): void {
    const d = fieldZone1(), g = d.grid;
    const c = document.createElement('canvas');
    c.width = g.width; c.height = g.height;
    const x = c.getContext('2d');
    if (!x) return;
    const img = x.createImageData(g.width, g.height);
    for (let i = 0; i < g.cells.length; i++) {
      const v = g.cells[i];
      const o = i * 4;
      if (v === 1) { img.data[o] = 120; img.data[o + 1] = 134; img.data[o + 2] = 96; img.data[o + 3] = 255; }
      else if (v === 2) { img.data[o] = 70; img.data[o + 1] = 68; img.data[o + 2] = 60; img.data[o + 3] = 255; }
      else { img.data[o] = 12; img.data[o + 1] = 16; img.data[o + 2] = 24; img.data[o + 3] = 255; }
    }
    x.putImageData(img, 0, 0);
    this.bg = c;
  }

  update(u: number, nowMs: number): void {
    const v = this.view, fv = v.field, sc = v.flow.scene;
    const on = !!fv && (sc === Scene.Field || sc === Scene.Rift) && !v.flow.paused;
    // 보이기 상태 하나(0 숨김, 1 필드, 2 균열)로 두 패널을 같이 맞춘다 — 일시정지·대기 화면을 거쳐도 균열 게이지가 다시 보인다
    const rift = sc === Scene.Rift;
    const mode = !on ? 0 : rift ? 2 : 1;
    if (mode !== this.dispMode) {
      this.dispMode = mode;
      this.root.style.display = mode === 1 ? 'flex' : 'none';
      this.rift.style.display = mode === 2 ? 'flex' : 'none';
      if (mode === 0) this.band.style.display = 'none';
      if (mode !== 1) { this.choice.style.display = 'none'; this.choiceOpen = false; }
      this.lastRiftKey = -1;
      this.riftLayoutKey.fill(-1);
      if (mode === 1) { this.cuePhase = -1; this.cuePortal = false; }
    }
    if (!on || !fv) return;
    if (mode === 1) this.layoutField();
    if (u !== this.lastU) { this.lastU = u; const t = 'scale(' + Math.max(0.8, Math.min(1.1, u)).toFixed(3) + ')'; this.root.style.transform = t; this.rift.style.transform = t; }
    // 알림 띠: 새 이벤트(이벤트 번호가 오르면)
    const e = fv.event;
    if (e.serial !== this.eventSerial) {
      if (this.eventSerial >= 0 && e.kind !== FieldEventKind.None) this.showBand(e.kind === FieldEventKind.Elite ? '필드 이벤트 · 엘리트 무리 출현!' : '필드 이벤트 · 보물 고블린 출현! 놓치지 마세요', nowMs);
      this.eventSerial = e.serial;
    }
    if (nowMs > this.bandUntil && this.band.style.display !== 'none') this.band.style.display = 'none';
    this.updateGrift(fv.rift, !rift, nowMs);
    if (rift) { this.updateRift(nowMs); return; }
    // 글자는 값이 바뀐 프레임에만 만든다(청크·초·조각)
    const hc = fv.heroChunk;
    if (hc !== this.lastChunk) {
      this.lastChunk = hc;
      const zone = fv.zoneName + ' · 구역 1' + (hc >= 0 ? ' · ' + COLS[hc % G.cols] + (Math.floor(hc / G.cols) + 1) : '');
      if (zone !== this.lastZone) { this.lastZone = zone; this.zone.textContent = zone; }
    }
    if (fv.shards !== this.lastShards) {
      this.lastShards = fv.shards;
      this.shards.innerHTML = '<span style="color:#c9a0ff;font-size:15px">◆</span> 균열 조각 <b style="color:' + (fv.shards >= RIFT.shards ? '#d9b8ff' : '#f4ecd8') + '">' + fv.shards + '</b>/' + RIFT.shards;
    }
    const evSec = e.kind === FieldEventKind.None ? 0 : Math.ceil(e.remainTicks / 60);
    if (e.kind !== this.lastEvKind || evSec !== this.lastEvSec) {
      this.lastEvKind = e.kind; this.lastEvSec = evSec;
      const evText = e.kind === FieldEventKind.None ? '' : (e.kind === FieldEventKind.Elite ? '엘리트 무리' : '보물 고블린') + ' · ' + evSec + '초 · 남은 ' + e.alive;
      if (evText !== this.lastEvent) { this.lastEvent = evText; this.eventLine.style.display = evText ? 'block' : 'none'; this.eventLine.textContent = '❖ ' + evText; }
    }
    const hold = Math.round(fv.returnHold * 20);
    if (hold !== this.lastHold) { this.lastHold = hold; this.hold.style.display = hold > 0 ? 'block' : 'none'; this.holdBar.style.width = (hold * 5) + '%'; }
    if ((this.frame++ & 1) === 0) this.drawMap(nowMs);
  }

  /** 위 줄에서 알림 띠가 비켜야 할 HUD(게임 화면 좌표). 알림이 뜰 때만 읽는다 */
  private readonly bandObs: Array<{ x: number; y: number; w: number; h: number }> = [];
  private bandObstacles(): Array<{ x: number; y: number; w: number; h: number }> {
    const out = this.bandObs;
    out.length = 0;
    const rot = viewRotated(), sw = window.innerWidth;
    for (const id of TOP_BAND_AVOID) {
      const e = this.parentEl.querySelector<HTMLElement>('[data-testid="' + id + '"]');
      if (!e) continue;
      const b = e.getBoundingClientRect();
      if (b.width < 1 || b.height < 1) continue;
      const m = mapRect(rot, sw, b);
      out.push({ x: m.left, y: m.top, w: m.width, h: m.height });
    }
    return out;
  }

  private showBand(text: string, nowMs: number, good = false, ms = BAND_MS): void {
    if (text !== this.lastBand) { this.lastBand = text; this.band.textContent = text; }
    const b = this.band, st = b.style;
    st.borderColor = good ? '#f2c14e' : '#c0503a';
    // 자리: 한 줄 자연 폭을 재고 위 줄 다른 HUD를 비킨 빈 구간에 놓는다(안 들어가면 두 줄)
    st.display = 'block'; st.width = 'auto'; st.whiteSpace = 'nowrap'; st.height = TOP_BAND_H1 + 'px'; st.padding = '0 16px'; st.lineHeight = '32px'; st.transform = 'none';
    const slot = topBandSlot(viewW(), b.scrollWidth + 4, this.bandObstacles());
    st.left = slot.x + 'px';
    if (slot.wrap) { st.width = slot.w + 'px'; st.whiteSpace = 'normal'; st.height = 'auto'; st.padding = '4px 12px'; st.lineHeight = '17px'; st.textOverflow = 'clip'; }
    else { st.width = slot.w + 'px'; st.textOverflow = 'ellipsis'; }
    this.bandUntil = nowMs + ms;
  }

  /** 대균열(C12): 결과 알림 띠(결과 값이 바뀔 때 한 번)와 입구 선택 띠(필드에서 choice일 때). 값이 바뀔 때만 DOM을 고친다 */
  private updateGrift(r: RiftView, inField: boolean, nowMs: number): void {
    if (r.result !== this.lastResult) {
      if (this.lastResult >= 0 && r.result > 0) { const t = resultText(r); if (t) this.showBand(t, nowMs, r.result === 1, 7000); }
      this.lastResult = r.result;
    }
    const open = inField && r.choice;
    if (open !== this.choiceOpen) {
      this.choiceOpen = open;
      this.choice.style.display = open ? 'flex' : 'none';
      // 입구 선택 띠가 떠 있는 동안은 필드 HUD 기둥(구역·미니맵·조각)을 접는다: 띠가 조각 수(일반 균열 · 조각 n/5)를 이미 보여 주고,
      // 좁은 터치 화면에는 둘이 함께 들어갈 가장자리 빈 자리가 없다(2026-11-02 겹침 측정). 닫으면 바로 다시 펼친다
      this.root.style.display = open ? 'none' : 'flex';
      if (open) { this.gTier = r.selectable; this.choiceKey.fill(-1); this.sizeKey.fill(-1); }
    }
    if (!open) return;
    const fv = this.view.field!;
    // 원시 입력을 숫자로 먼저 비교하고, 바뀐 프레임에만 모델·문자열을 만든다(프레임당 할당 0)
    if (choiceInputsChanged(this.choiceKey, r, fv.shards, this.gTier)) {
      const m = choiceModel(r, fv.shards, RIFT.shards, this.gTier);
      this.gTier = m.tier; this.choiceKey[5] = m.tier; // 고른 단계가 한도로 잘렸으면 잘린 값을 기준으로 삼아 다음 프레임에 다시 만들지 않는다
      this.cNormal.textContent = m.normalText; setBtn(this.cNormal, m.normalEnabled);
      this.cTier.textContent = m.tierText; setBtn(this.cMinus, m.minusEnabled); setBtn(this.cPlus, m.plusEnabled);
      this.cInfo.textContent = m.keysText + ' · ' + m.bestText + '\n' + m.capText + (m.hint ? ' · ' + m.hint : '');
      this.cGo.textContent = m.challengeText; setBtn(this.cGo, m.challengeEnabled);
    }
    const W = viewW(), H = viewH();
    // 키보드 화면은 스킬 바 크기가 바뀌면(스킬 배움·쪽 전환) 다시 잰다 — 띠가 열려 있을 때만 읽는다
    if (this.keyboardHud) this.measureInto(GRIFT_KB_AVOID, this.kbEls, this.kbObs);
    // 터치 화면: 조작 배치(사용자 크기·지정 위치)가 다시 계산됐으면 띠 자리도 다시 잰다
    const tv = this.touch && !this.keyboardHud ? this.touch.layoutVersion : -1;
    if (tv !== this.touchVer) { this.touchVer = tv; this.sizeKey.fill(-1); }
    if (riftLayoutChanged(this.sizeKey, W, H, 1, this.keyboardHud ? this.kbObs : NO_RECTS)) {
      const st = getComputedStyle(this.parentEl);
      const safe = { left: parseFloat(st.paddingLeft) || 0, right: parseFloat(st.paddingRight) || 0, top: parseFloat(st.paddingTop) || 0, bottom: parseFloat(st.paddingBottom) || 0 };
      const p = touchHudLayout(W, H, designScale(W, H) * uiSizeFactor(W), safe);
      // 키보드 화면은 터치 조작 대신 가운데 아래 스킬 바(물약·각성 포함)가 바닥을 차지한다 → 실제 자리를 장애물로
      const ctrls = this.keyboardHud ? this.kbObs.filter((o) => o.w > 0 && o.h > 0) : this.touch ? this.touch.controlRects : touchControlRects(p);
      const b = bandLayout(W, H, this.keyboardHud ? { x: safe.left, y: H, w: 0, h: 0 } : p.joystick, ctrls, safe);
      if (b.rows !== this.choiceRows) {
        this.choiceRows = b.rows;
        if (b.rows >= 3) { this.choiceR3.appendChild(this.cInfo); this.choiceR3.style.display = 'flex'; }
        else { this.choiceR2.insertBefore(this.cInfo, this.cGo); this.choiceR3.style.display = 'none'; }
      }
      const cs = this.choice.style;
      cs.left = b.x + 'px'; cs.top = b.y + 'px'; cs.width = b.w + 'px'; cs.height = b.h + 'px';
    }
  }

  /** 균열 진행 시계 자리: 터치 조작(각성 게이지 등)과 겹치면 옆으로 민다(riftHudSlot). 화면 크기가 바뀔 때만 다시 잰다 */
  /** 필드 HUD 기둥(구역·조각·미니맵) 자리: 터치 조작과 겹치면 옆으로 민다(columnSlot). 크기·내용 폭이 바뀔 때만 다시 잰다 */
  private layoutField(): void {
    const W = viewW(), H = viewH(), u = designScale(W, H) * uiSizeFactor(W), rw = this.root.offsetWidth, rh = this.root.offsetHeight;
    // 숫자 키 비교(매 프레임 문자열을 만들지 않는다). rw<1(아직 안 보임)이면 키를 건드리지 않고 나간다
    if (rw < 1) return;
    const fk = this.fieldKey;
    if ((setKey(fk, 0, W) | setKey(fk, 1, H) | setKey(fk, 2, Math.round(u * 1000)) | setKey(fk, 3, rw) | setKey(fk, 4, rh)) === 0) return;
    const st = getComputedStyle(this.parentEl);
    const safe = { left: parseFloat(st.paddingLeft) || 0, right: parseFloat(st.paddingRight) || 0, top: parseFloat(st.paddingTop) || 0, bottom: parseFloat(st.paddingBottom) || 0 };
    const s = Math.max(0.8, Math.min(1.1, u));
    const slot = columnSlot(W, s, riftControls(touchHudLayout(W, H, u, safe)), safe, 118, [rw], rh);
    this.root.style.right = slot.right + 'px';
  }

  /** 균열 시계 자리 계산용 장애물(위 오른쪽 패널·미니맵): 한 번 만든 객체·요소를 재사용한다(프레임당 할당 0) */
  private readonly riftObs: BandRect[] = [{ x: 0, y: 0, w: 0, h: 0 }, { x: 0, y: 0, w: 0, h: 0 }];
  private readonly riftObsEls: Array<HTMLElement | null> = [null, null];
  private readonly riftObsIds: readonly string[] = ['hud-top-right', 'hud-minimap'];
  private readonly riftLayoutKey = new Float64Array(7).fill(-1);
  private readonly viewTmp = { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  private measureRiftObs(): void { this.measureInto(this.riftObsIds, this.riftObsEls, this.riftObs); }
  /** ids의 요소를 (캐시한 참조로) 재서 out 버퍼에 채운다. 없거나 안 보이면 0 크기 */
  private measureInto(ids: readonly string[], els: Array<HTMLElement | null>, out: BandRect[]): void {
    const rot = viewRotated(), sw = window.innerWidth;
    for (let i = 0; i < out.length; i++) {
      const o = out[i];
      let e = els[i];
      if (!e || !e.isConnected) e = els[i] = this.parentEl.querySelector<HTMLElement>('[data-testid="' + ids[i] + '"]');
      if (!e) { o.x = o.y = o.w = o.h = 0; continue; }
      const b = e.getBoundingClientRect();
      if (b.width < 1 || b.height < 1) { o.x = o.y = o.w = o.h = 0; continue; }
      const m = mapRect(rot, sw, b, this.viewTmp);
      o.x = m.left; o.y = m.top; o.w = m.width; o.h = m.height;
    }
  }

  private layoutRift(): void {
    const W = viewW(), H = viewH(), u = designScale(W, H) * uiSizeFactor(W);
    this.measureRiftObs();
    // 크기·배율·장애물 위치가 바뀐 프레임에만 아래 계산(객체 만들기)을 한다
    if (!riftLayoutChanged(this.riftLayoutKey, W, H, u, this.riftObs)) return;
    const obs = this.riftObs;
    const st = getComputedStyle(this.parentEl);
    const safe = { left: parseFloat(st.paddingLeft) || 0, right: parseFloat(st.paddingRight) || 0, top: parseFloat(st.paddingTop) || 0, bottom: parseFloat(st.paddingBottom) || 0 };
    const s = Math.max(0.8, Math.min(1.1, u));
    const p = touchHudLayout(W, H, u, safe);
    let slot = riftHudSlot(W, s, riftControls(p), safe);
    // 오른쪽 위 패널(영웅 정보·미니맵)과 겹치면 그 아래로 내린다(키보드 화면에서 미니맵과 겹쳤다)
    const x1 = W - slot.right;
    const top = riftHudTop(slot.top, x1 - slot.width * s, x1, RIFT_HUD_H * s, obs);
    if (top !== slot.top) slot = { ...slot, top };
    this.rift.style.right = slot.right + 'px'; this.rift.style.width = slot.width + 'px'; this.rift.style.top = slot.top + 'px';
  }

  /** 입구 선택 띠가 열려 있는가(연계 안내 줄이 같은 자리를 덮지 않게 HUD가 읽는다) */
  get choiceVisible(): boolean { return this.choiceOpen; }

  private updateRift(nowMs: number): void {
    this.layoutRift();
    const r = this.view.field!.rift;
    // 알림 띠는 단계가 바뀌는 순간 한 번만(대균열은 머리글이 '대균열 n단계…'라 글자 비교로는 알 수 없다)
    const cue = riftBandCue(this.cuePhase, r.phase, this.cuePortal, r.portalActive);
    this.cuePhase = r.phase; this.cuePortal = r.portalActive;
    if (cue === 1) this.showBand('균열 수호자 등장!', nowMs);
    else if (cue === 2) this.showBand('다음 층 통로가 열렸다', nowMs);
    const left = Math.max(0, r.limitTicks - r.elapsedTicks), late = r.elapsedTicks > r.limitTicks;
    const sec = Math.ceil(left / 60);
    // 값(단계·층·게이지 %·초·통로)이 바뀐 프레임에만 글자를 만든다
    const rk = ((((r.phase * 8 + r.floor) * 101 + Math.floor(r.gauge * 100)) * 4096 + (late ? 4095 : sec)) * 2) + (r.portalActive ? 1 : 0);
    const rk2 = rk * 64 + r.tier;
    if (rk2 === this.lastRiftKey) return;
    this.lastRiftKey = rk2;
    const gh = griftHead(r.tier, r.gauge), gc = griftClock(r.limitTicks, r.elapsedTicks);
    const head = gh ?? (r.phase === RiftPhase.Guardian ? '균열 수호자' : r.phase >= RiftPhase.Chest ? '균열 정화' : '균열 ' + (r.floor + 1) + '/' + r.floors + '층') + ' · ' + Math.floor(r.gauge * 100) + '%';
    const time = r.tier > 0 ? gc.text : late ? '시간 초과' : Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
    const key = head + '|' + time;
    if (key !== this.lastRift) {
      this.lastRift = key;
      this.riftHead.textContent = head; this.riftTime.textContent = time;
      this.riftTime.style.color = (r.tier > 0 ? gc.warn : late) ? '#ff6a5a' : '#f0e4ff';
      this.riftBar.style.width = Math.round(r.gauge * 100) + '%';
    }
  }

  /** 미니맵 점(월드 좌표). 화살표 함수 필드라 프레임마다 새로 만들지 않는다 */
  private readonly dot = (x: CanvasRenderingContext2D, wx: number, wz: number, r: number, col: string): void => {
    x.fillStyle = col; x.beginPath(); x.arc((wx - this.mapOx) * this.mapScale, (wz - this.mapOz) * this.mapScale, r, 0, Math.PI * 2); x.fill();
  };

  private drawMap(nowMs: number): void {
    const x = this.ctx, fv = this.view.field!, v = this.view;
    if (!x) return;
    if (!this.bg) this.drawBg();
    const d = fieldZone1(), s = (MAP_PX * 2) / d.grid.width;
    this.mapScale = s; this.mapOx = d.grid.originX; this.mapOz = d.grid.originZ;
    x.clearRect(0, 0, MAP_PX * 2, MAP_PX * 2);
    if (this.bg) { x.imageSmoothingEnabled = false; x.drawImage(this.bg, 0, 0, MAP_PX * 2, MAP_PX * 2); }
    // 활성 3×3
    x.strokeStyle = 'rgba(242,193,78,.55)'; x.lineWidth = 2;
    for (let k = 0; k < fv.activeCount; k++) {
      const c = fv.activeChunks[k], cx = c % G.cols, cz = Math.floor(c / G.cols);
      x.strokeRect(cx * G.chunk * s + 1, cz * G.chunk * s + 1, G.chunk * s - 2, G.chunk * s - 2);
    }
    const dot = this.dot;
    dot(x, d.returnStone.x, d.returnStone.z, 4, '#7fd4ff');
    dot(x, d.riftPortal.x, d.riftPortal.z, 5, fv.shards >= RIFT.shards ? '#e0b0ff' : '#8a5cc0');
    dot(x, (d.exit.x0 + d.exit.x1) / 2, d.exit.z1 - 1, 4, '#4fb6d0');
    if (fv.hiddenFound) dot(x, d.hidden.x, d.hidden.z, 4, '#9be06a');
    // 근처 몹(빨간 점)
    const m = v.mobs;
    for (let i = 0; i < m.capacity; i++) if (m.state[i] !== 0 && m.state[i] !== 8) dot(x, m.x[i], m.z[i], 1.8, '#ff6a5a');
    const e = fv.event;
    if (e.kind !== FieldEventKind.None) {
      const pulse = 5 + Math.sin(nowMs / 160) * 1.5;
      x.strokeStyle = e.kind === FieldEventKind.Elite ? '#ff4040' : '#ffd040'; x.lineWidth = 3;
      x.beginPath(); x.arc((e.x - this.mapOx) * s, (e.z - this.mapOz) * s, pulse + 3, 0, Math.PI * 2); x.stroke();
      dot(x, e.x, e.z, 4, e.kind === FieldEventKind.Elite ? '#ff4040' : '#ffd040');
    }
    // 영웅(흰 삼각형, 바라보는 방향)
    const h = v.hero, hx = (h.x - this.mapOx) * s, hz = (h.z - this.mapOz) * s, f = h.facing;
    x.fillStyle = '#ffffff';
    x.beginPath();
    x.moveTo(hx + Math.sin(f) * 7, hz + Math.cos(f) * 7);
    x.lineTo(hx + Math.sin(f + 2.5) * 5, hz + Math.cos(f + 2.5) * 5);
    x.lineTo(hx + Math.sin(f - 2.5) * 5, hz + Math.cos(f - 2.5) * 5);
    x.closePath(); x.fill();
  }
}
